import React from 'react';
import { SiteSection, SiteSectionHeader } from '../../components/site';
import type { SolutionBenefitOrbit, SolutionBenefitOrbitPosition } from './types';

const NODE_COORDS: Record<SolutionBenefitOrbitPosition, { x: number; y: number }> = {
  top: { x: 500, y: 88 },
  left: { x: 138, y: 360 },
  right: { x: 862, y: 360 },
  bottom: { x: 500, y: 632 },
};

const HUB = { x: 500, y: 360 };

function nodeByPosition(nodes: SolutionBenefitOrbit['nodes'], position: SolutionBenefitOrbitPosition) {
  return nodes.find((node) => node.position === position);
}

export const SolutionCommercialBenefitOrbit: React.FC<{
  orbit: SolutionBenefitOrbit;
}> = ({ orbit }) => (
  <SiteSection id="advantages" className="cl-benefit-orbit">
    <SiteSectionHeader
      eyebrow={orbit.eyebrow}
      title={orbit.title}
      subtitle={orbit.subtitle}
      align="center"
    />

    <div className="cl-benefit-orbit__stage">
      <svg className="cl-benefit-orbit__lines" viewBox="0 0 1000 720" aria-hidden>
        <defs>
          <linearGradient id="cl-orbit-line" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0.2" />
            <stop offset="50%" stopColor="#4FD1FF" stopOpacity="0.65" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        {orbit.nodes.map((node, index) => {
          const end = NODE_COORDS[node.position];
          return (
            <g key={node.id}>
              <line
                x1={HUB.x}
                y1={HUB.y}
                x2={end.x}
                y2={end.y}
                stroke="url(#cl-orbit-line)"
                strokeWidth="2"
                strokeDasharray="6 8"
                className="cl-benefit-orbit__line"
                style={{ animationDelay: `${index * 0.25}s` }}
              />
              <circle
                className="cl-benefit-orbit__pulse"
                cx={end.x}
                cy={end.y}
                r="4"
                style={{ animationDelay: `${index * 0.3}s` }}
              />
            </g>
          );
        })}
      </svg>

      {(['top', 'left', 'right', 'bottom'] as const).map((position) => {
        const node = nodeByPosition(orbit.nodes, position);
        if (!node) return null;
        return (
          <article
            key={node.id}
            className={`cl-benefit-orbit__node cl-benefit-orbit__node--${position}`}
          >
            <span className="cl-benefit-orbit__node-emoji" aria-hidden>
              {node.emoji}
            </span>
            <h4 className="cl-benefit-orbit__node-title">{node.title}</h4>
            <p className="cl-benefit-orbit__node-desc">{node.description}</p>
          </article>
        );
      })}

      <div className="cl-benefit-orbit__hub" aria-label={orbit.hubTitle}>
        <span className="cl-benefit-orbit__hub-glow" aria-hidden />
        <span className="cl-benefit-orbit__hub-ring" aria-hidden />
        <span className="cl-benefit-orbit__hub-icon" aria-hidden>
          💡
        </span>
        <h3 className="cl-benefit-orbit__hub-title">{orbit.hubTitle}</h3>
        {orbit.hubSubtitle ? (
          <p className="cl-benefit-orbit__hub-sub">{orbit.hubSubtitle}</p>
        ) : null}
      </div>
    </div>

    <ul className="cl-benefit-orbit__metrics" aria-label="Platform highlights">
      {orbit.metrics.map((metric) => (
        <li key={metric.label} className="cl-benefit-orbit__metric">
          <span className="cl-benefit-orbit__metric-value">{metric.value}</span>
          <span className="cl-benefit-orbit__metric-label">{metric.label}</span>
        </li>
      ))}
    </ul>
  </SiteSection>
);
